import React, { useState } from 'react';
import { ContentWrapper } from 'app/pages/library/videos/videoList/components/SendAndShare/send/SendTab';
import {
  LoadingIndicator,
  ModalVideoListVideo,
  TableContextProvider,
  Search,
  TablePaginationNew,
  ModalDelete,
} from 'lib/components';
import { Button } from 'react-covideo-common';
import { Gap } from '../../styles/layout';
import { ParagraphSmallBold } from '../../styles/typography';
import styled, { css } from 'styled-components/macro';
import { theme } from 'lib/style';
import { VideoItemThumbnail } from 'app/pages/library/videos/components';
import { debounce } from 'lodash';
import { MultiselectDropdown } from 'lib/components/dropdown/MultiselectDropdown';
import { PaginationConstants } from 'lib/const/PaginationConstants';
import { SortButtons } from 'app/pages/admin/components/SortButtons';
import {
  OrganizationVideo,
  OrganizationVideoSortKey,
  useOrganizationVideosQuery,
} from 'lib/api/organizationVideo/useOrganizationVideoQuery';
import { useOrganizationVideoFiltersQuery } from 'lib/api/organizationVideo/useOrganizationVideoFiltersQuery';
import { MdAdd, MdDeleteForever, MdEdit, MdShortText } from 'react-icons/md';
import { ModalOrganizationVideoDetails } from './ModalOrganizationVideoDetails';
import { useOrganizationVideoImportMutation } from 'lib/api/organizationVideo/useImportOrganizationVideoMutation';
import { useAuth } from 'lib/context';
import { useArchiveOrganizationVideoMutation } from 'lib/api/organizationVideo/useArchiveOrganizationVideoMutation';
import { useToastError } from 'lib/hooks';
import { ModalOrganizationVideoForm } from './ModalOrganizationVideoForm';
import { BsPlayFill } from 'react-icons/bs';

const Content = styled(ContentWrapper)`
  margin-top: 0;
  display: flex;
  flex-direction: column;
  width: 1200px;
  padding-bottom: 0;
`;

const VideosList = styled.div`
  width: 100%;
  min-height: 530px;
`;

const Video = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  height: 56px;
  border-bottom: 1px solid rgb(223, 227, 230);
`;

const VideoPlay = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const VideoThumbnailWrapper = styled.div`
  position: relative;
  width: 60px;
  height: 40px;
  border-radius: 5px;
  background: ${theme.palette.black};
  cursor: pointer;
  overflow: hidden;
`;

const BaseModalText = styled.div`
  height: 24px;
  margin-left: 24px;
  font-size: 16px;
  line-height: 1.5;
  text-align: left !important;
  color: ${theme.palette.title};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

const BaseHeaderText = styled(BaseModalText)`
  font-size: 14px;
  color: ${theme.palette.blackRgb60};
  display: flex;
`;

// vehicle year
const VehicleYearWidth = css`
  max-width: 80px;
  min-width: 80px;
`;
const VehicleYear = styled(BaseModalText)`
  ${VehicleYearWidth}
`;

const VehicleYearHeader = styled(BaseHeaderText)`
  ${VehicleYearWidth}
`;
// vehicle make
const VehiclePlaylistWidth = css`
  max-width: 100px;
  min-width: 100px;
`;
const VehiclePlaylist = styled(BaseModalText)`
  ${VehiclePlaylistWidth}
`;
const VehiclePlaylistHeader = styled(BaseHeaderText)`
  ${VehiclePlaylistWidth}
`;

// vehicle preview
const VehiclePreviewWidth = css`
  min-width: 80px;
  max-width: 80px;
`;
const VehiclePreview = styled.div`
  ${VehiclePreviewWidth}
`;
const VehiclePreviewHeader = styled(BaseHeaderText)`
  margin-left: 0;
  ${VehiclePreviewWidth}
`;
// vehicle library
const VehicleLibraryWidth = css`
  min-width: 100px;
  max-width: 100px;
`;
const VehicleLibrary = styled(BaseModalText)`
  ${VehicleLibraryWidth}
`;
const VehicleLibraryHeader = styled(BaseHeaderText)`
  ${VehicleLibraryWidth}
`;

// vehicle model
const VehicleModelWidth = css`
  max-width: 180px;
  min-width: 180px;
`;
const VehicleModel = styled(BaseModalText)`
  ${VehicleModelWidth}
`;

const VehicleModelHeader = styled(BaseHeaderText)`
  ${VehicleModelWidth}
`;

// vehicle title
const VehicleTitleWidth = css`
  width: inherit;
`;
const VehicleTitle = styled(BaseModalText)`
  ${VehicleTitleWidth}
  cursor: default;
`;

const VehicleTitleHeader = styled(BaseHeaderText)`
  ${VehicleTitleWidth}
`;

const EmptySearch = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  min-height: 530px;
`;

const Footer = styled(ContentWrapper)`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
  padding-bottom: 0;
`;

type Props = {
  showAddToCovideo?: boolean;
  handleModalClose?: () => void;
  onCopy?: () => void;
  onSelectVideo?: (organizationVideo: OrganizationVideo) => void;
};

const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 10;

export const OrganizationVideosTable = ({
  handleModalClose,
  onCopy,
  showAddToCovideo = true,
  onSelectVideo,
}: Props) => {
  const {
    userData: { userOrganizationAccess },
  } = useAuth();
  const { showError } = useToastError();
  const [searchTerm, setSearchTerm] = useState('');
  const [videoSource, setVideoSource] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [filter, setFilter] = useState<{
    [key: string]: string[];
  }>({});
  //sorting
  const [sortKey, setSortKey] = useState<OrganizationVideoSortKey | null>(null);
  const [order, setOrder] = useState(PaginationConstants.ASCENDING);
  const [showVideoDetails, setShowVideoDetails] =
    useState<OrganizationVideo | null>(null);
  const [showDeleteModal, setShowDeleteModal] =
    useState<OrganizationVideo | null>(null);
  const [showEditModal, setShowEditModal] = useState<OrganizationVideo | null>(
    null
  );

  const resetPage = () => {
    setPage(DEFAULT_PAGE);
  };

  const onSuccesCallback = () => {
    onCopy?.();
    handleModalClose?.();
  };

  //queries
  const { data, isLoading: isLoadingVideos } = useOrganizationVideosQuery(
    {
      limit: DEFAULT_SIZE,
      page,
      search: searchTerm,
      filter,
      ...(!!sortKey && !!order ? { sortKey, order } : {}),
    },
    true,
    handleModalClose
  );
  const { data: filterData, isLoading: isLoadingFilters } =
    useOrganizationVideoFiltersQuery();

  // mutations
  const { isLoading: isImportingVideo, mutateAsync: importOrganizationVideo } =
    useOrganizationVideoImportMutation({
      onSuccesCallback,
    });

  const { mutateAsync: archiveOrganizationVideo, isLoading: isArchivingVideo } =
    useArchiveOrganizationVideoMutation();

  const isLoading = isLoadingVideos || isImportingVideo || isLoadingFilters;

  const onSearchChangeHandler = async (search: string = '') => {
    setSearchTerm(search);
    resetPage();
  };

  const onPaginationChangeHandler = debounce(
    async ({ page: newPage }: { page: number }) => {
      if (page !== newPage) {
        setPage(newPage);
      }
    },
    300
  );

  const copyToCovideoHandler = (video: OrganizationVideo) => {
    importOrganizationVideo({
      organizationVideoId: video.organizationVideoId,
    });
  };

  const showSelectVideo = !!onSelectVideo;

  const handleFilterChange = (key: string, selectedOption: string[]) => {
    if (key === 'make') {
      setFilter(prevState => ({
        ...prevState,
        [key]: selectedOption,
        model: [],
      }));
      return;
    }
    setFilter(prevState => ({
      ...prevState,
      [key]: selectedOption,
    }));
  };

  const handleSortChange = (
    sortKey: OrganizationVideoSortKey,
    order: PaginationConstants
  ) => {
    setOrder(order);
    setSortKey(sortKey);
    setPage(DEFAULT_PAGE);
  };

  const getEmptyStateMessage = () => {
    const hasFilters = Object.values(filter).some(
      val => Array.isArray(val) && val.length > 0
    );

    if (searchTerm && hasFilters) {
      return {
        primary: `No videos found matching "${searchTerm}" with your selected filters.`,
        secondary:
          'Try adjusting your filters or using a different search term.',
      };
    }
    if (searchTerm) {
      return {
        primary: `There are no videos with "${searchTerm}" in the title.`,
        secondary: 'Try using a different search term.',
      };
    }
    if (hasFilters) {
      return {
        primary: 'No videos match your selected filters.',
        secondary: 'Try selecting different filter options.',
      };
    }
    return {
      primary:
        "Unfortunately, there aren't any videos available at the moment.",
      secondary: '',
    };
  };

  const checkEditVideoPermission = (video: OrganizationVideo) => {
    if (showSelectVideo) {
      return false; // hide crud on merge modal
    }
    return !!(
      !!userOrganizationAccess &&
      userOrganizationAccess.length &&
      userOrganizationAccess.some(
        access => access.organizationId === video.organizationId.toString()
      )
    );
  };

  const handleSubmitArchiveVideo = async (video: OrganizationVideo) => {
    try {
      await archiveOrganizationVideo(video.organizationVideoId);
      setShowDeleteModal(null);
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Content>
      <Gap
        m='0 0 20px auto'
        alignItems='flex-end'
        justifyContent='space-between'
        width='100%'
      >
        {filterData && (
          <Gap gap='12px'>
            {filterData.filters?.map(item => {
              return (
                <MultiselectDropdown
                  key={item.key}
                  onOptionsSelected={value =>
                    handleFilterChange(item.key, value as string[])
                  }
                  options={item.options}
                  menuListValue={-1}
                  menuListLabel={`All ${item.name === 'Library' ? 'Libraries' : `${item.name}s`}`}
                  name={`${item.name}`}
                  menuShouldBlockScroll={false}
                  isLoading={isLoadingFilters}
                  selectInputWidth='200px'
                  menuWidth='280px'
                  isDisabled={isLoadingFilters}
                  isSearchable
                  isClearable
                />
              );
            })}
          </Gap>
        )}
        <Search
          placeholder='Search videos...'
          onSearch={onSearchChangeHandler}
          width='300px'
          disabled={isImportingVideo}
        />
      </Gap>
      {isLoading ? (
        <div>
          <LoadingIndicator isLoading={isLoading} />
        </div>
      ) : (
        <>
          {!data?.videos?.length ? (
            <EmptySearch>
              {(() => {
                const message = getEmptyStateMessage();
                return (
                  <>
                    <ParagraphSmallBold color={theme.palette.blue60}>
                      {message.primary}
                    </ParagraphSmallBold>
                    {message.secondary && (
                      <ParagraphSmallBold color={theme.palette.blue60}>
                        {message.secondary}
                      </ParagraphSmallBold>
                    )}
                  </>
                );
              })()}
            </EmptySearch>
          ) : (
            <>
              <VideosList>
                <Video>
                  <VehiclePreviewHeader>Preview</VehiclePreviewHeader>
                  <VehicleLibraryHeader>
                    Library
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.LIBRARY}
                      order={order}
                      onChange={value =>
                        handleSortChange(
                          OrganizationVideoSortKey.LIBRARY,
                          value
                        )
                      }
                    />
                  </VehicleLibraryHeader>
                  <VehiclePlaylistHeader>
                    Playlist
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.PLAYLIST}
                      order={order}
                      onChange={value =>
                        handleSortChange(
                          OrganizationVideoSortKey.PLAYLIST,
                          value
                        )
                      }
                    />
                  </VehiclePlaylistHeader>
                  <VehicleModelHeader>
                    Model
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.MODEL}
                      order={order}
                      onChange={value =>
                        handleSortChange(OrganizationVideoSortKey.MODEL, value)
                      }
                    />
                  </VehicleModelHeader>
                  <VehicleYearHeader>
                    Year
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.YEAR}
                      order={order}
                      onChange={value =>
                        handleSortChange(OrganizationVideoSortKey.YEAR, value)
                      }
                    />
                  </VehicleYearHeader>
                  <VehicleTitleHeader>
                    Title
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.TITLE}
                      order={order}
                      onChange={value =>
                        handleSortChange(OrganizationVideoSortKey.TITLE, value)
                      }
                    />
                  </VehicleTitleHeader>
                </Video>
                {data?.videos?.map(video => (
                  <Video key={video.organizationVideoId}>
                    <VehiclePreview>
                      {video.thumbnailUrl ? (
                        <VideoItemThumbnail
                          handleClick={() => {
                            if (!!video.videoUrl) {
                              setVideoSource(video.videoUrl);
                            }
                          }}
                          thumbnail={video.thumbnailUrl}
                          videoId={''}
                          processing={0}
                          thumbnailWidth={'60px'}
                          thumbnailHeight={'40px'}
                        />
                      ) : (
                        <VideoThumbnailWrapper
                          onClick={() => {
                            if (!!video.videoUrl) {
                              setVideoSource(video.videoUrl);
                            }
                          }}
                        >
                          <video
                            src={video.videoUrl}
                            width={60}
                            height={40}
                            controls={false}
                          />

                          <VideoPlay>
                            <BsPlayFill color={theme.palette.white} size={30} />
                          </VideoPlay>
                        </VideoThumbnailWrapper>
                      )}
                    </VehiclePreview>
                    <VehicleLibrary title={video.library}>
                      {video.library}
                    </VehicleLibrary>
                    <VehiclePlaylist title={video.playlist}>
                      {video.playlist}
                    </VehiclePlaylist>
                    <VehicleModel title={video.model}>
                      {video.model}
                    </VehicleModel>
                    <VehicleYear>{video.year}</VehicleYear>
                    <VehicleTitle title={video.title}>
                      {video.title}
                    </VehicleTitle>
                    <Gap flexWrap='noWrap' gap='10px'>
                      <>
                        {checkEditVideoPermission(video) && (
                          <>
                            <Button
                              variant='destructive'
                              icon={<MdDeleteForever />}
                              onClick={() => setShowDeleteModal(video)}
                              title={'Delete Video'}
                            />
                            <Button
                              variant='secondary'
                              icon={<MdEdit />}
                              onClick={() => setShowEditModal(video)}
                              title={'Edit Video'}
                            />
                          </>
                        )}
                        {!!video.description && (
                          <Button
                            variant='secondary'
                            icon={<MdShortText size={16} />}
                            onClick={() => setShowVideoDetails(video)}
                            title={'Show Video Description'}
                          />
                        )}
                        {showAddToCovideo && (
                          <Button
                            variant='primary'
                            icon={<MdAdd />}
                            onClick={() => copyToCovideoHandler(video)}
                            title={'Add to Covideo'}
                          />
                        )}
                        {showSelectVideo && (
                          <Button
                            variant='secondary'
                            text='Select Video'
                            onClick={() => onSelectVideo(video)}
                          />
                        )}
                      </>
                    </Gap>
                  </Video>
                ))}
              </VideosList>
              <Footer>
                <TableContextProvider
                  total={data?.count || 0}
                  initSize={DEFAULT_SIZE}
                  onChange={onPaginationChangeHandler}
                  initPage={page}
                >
                  <TablePaginationNew disabled={isLoading} />
                </TableContextProvider>
              </Footer>
            </>
          )}

          {!!videoSource && (
            <ModalVideoListVideo
              videoId={''}
              source={videoSource}
              handleModalClose={() => {
                setVideoSource('');
              }}
              hideContext={true}
            />
          )}

          {!!showVideoDetails && (
            <ModalOrganizationVideoDetails
              organizationVideo={showVideoDetails}
              handleModalClose={() => {
                setShowVideoDetails(null);
              }}
            />
          )}

          {!!showEditModal && (
            <ModalOrganizationVideoForm
              organizationVideo={showEditModal}
              handleModalClose={() => {
                setShowEditModal(null);
              }}
            />
          )}

          {showDeleteModal && (
            <ModalDelete
              title={`Delete Video`}
              orangeButtonText={'Keep it'}
              whiteButtonText={'Delete it'}
              text={`You are about to delete video ${showDeleteModal.title ? `"${showDeleteModal.title}"` : ''}`}
              hasConfirm={false}
              onClickOrangeButton={() => setShowDeleteModal(null)}
              onClickWhiteButton={() =>
                handleSubmitArchiveVideo(showDeleteModal)
              }
              handleModalClose={() => setShowDeleteModal(null)}
              loading={isArchivingVideo}
            />
          )}
        </>
      )}
    </Content>
  );
};
